/* A labelled text input, with its hint and its error.
 *
 * The markup and class names are exactly those in `design/components/text-field`
 * — the mockup is the specification, and keeping them identical is what lets
 * the two be diffed. What React adds is the wiring the mockup could only show
 * statically: `aria-describedby` assembled from whichever of hint and error is
 * present, and `aria-invalid` following the error.
 *
 * Every state the component authored is reachable here: default, focus,
 * disabled, error, and long content. There is no fixed width anywhere — a
 * translated label runs about 40% longer and must not be clipped.
 */

import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useId } from 'react';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly label: string;
  /** Shown before submission, not discovered after it. */
  readonly hint?: ReactNode;
  /** Present means the field is in its error state; absent means it is not. */
  readonly error?: string | undefined;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, ...input },
  ref,
) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  /* Both, when both exist. A field that describes only its error stops
   * announcing the requirement the moment somebody gets it wrong, which is
   * exactly when they need it. */
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  /* The error state is driven by `aria-invalid` on the control, not by a
   * modifier class on the wrapper — `text-field.css` styles
   * `.field__control[aria-invalid="true"]`. That is the better design and it
   * was the mockup's, not mine: the accessible state and the visual state
   * cannot drift apart, because they are the same attribute. */
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        {...input}
        ref={ref}
        id={id}
        className="field__control"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      />
      {hint ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        /* `role="alert"` so it is announced when it appears. Colour alone never
         * carries the meaning — the text is the message, and the border is the
         * reinforcement. */
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
});
